import { ContactCreateParams, GeoAutocompleteAddress, AddressHistorical } from '@brenger/api-client';
import { useDebounce } from '@brenger/react';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CacheKey } from '../../../types/enums';
import { coreClient } from '../../../utils/request';
import { InputText } from './InputText';
import cn from 'classnames';
import './address-book.scss';
import PlusCircle from '../../assets/images/plus-circle-blue.svg';
import { IconCross, IconSmile } from '../basics/icons';
import { Translate } from 'react-localize-redux-dep-updated';
import { translate } from '../../../utils/localization';
import { useTypedSelector } from '../../../hooks';
import { getAccountHasBusinessFlow } from '../../../modules/User/ducks';
import { useHighlight } from '../../../hooks/useHighlight';
import { IconSearchOutline } from '@brenger/react/icons/IconSearchOutline';

interface Props {
  onSelect: ({
    address,
    contact,
  }: {
    address: Partial<GeoAutocompleteAddress>;
    contact?: Partial<ContactCreateParams>;
    instructions?: string;
  }) => void;
  handleClose: () => void;
}

export const AddressBook = ({ onSelect, handleClose }: Props): JSX.Element => {
  const [query, setQuery] = React.useState('');
  const q = useDebounce(query, 500);

  const hasDepots = useTypedSelector(getAccountHasBusinessFlow);

  const addresses = useQuery({
    queryKey: [CacheKey.RETRIEVE_HISTORICAL_ADDRESS_LIST, q],
    queryFn: () => (hasDepots ? coreClient.users.listAddressesDepot(q) : coreClient.users.listAddressesHistorical(q)),
  });

  const handleSelect = (ha: AddressHistorical): void => {
    onSelect({
      address: {
        administrative_area: ha.administrative_area,
        country_code: ha.country_code,
        latitude: ha.lat,
        line1: ha.line1,
        line2: ha.line2,
        locality: ha.locality,
        longitude: ha.lng,
        postal_code: ha.postal_code,
      },
      contact: {
        first_name: ha.first_name,
        last_name: ha.last_name,
        phone: ha.phone,
        email: ha.depot_email_address || ha.email,
      },
      instructions: ha.instructions,
    });
  };
  const results = addresses.data?.['hydra:member'];

  const highlight = useHighlight(results?.map(ha => ({ action: () => handleSelect(ha) })));

  const id = 'address-book-search-input';
  return (
    <div className="address-book">
      <div className="address-book--header text-primary">
        <div className="relative">
          <button type="button" className="address-book--close" onClick={handleClose}>
            <IconCross style={{ width: '13px', height: '13px' }} />
          </button>
        </div>
        <h6 className="text-center">
          <Translate id="address_book.title" />
        </h6>
      </div>
      <label htmlFor={id} className="address-book--search flex items-center address-book--space">
        <IconSearchOutline />
        <InputText
          data-clarity-unmask="true"
          autoFocus={true}
          autoComplete={'off'}
          type={'text'}
          id={id}
          className="address-book--search-input"
          onChange={v => {
            highlight.reset();
            setQuery(v);
          }}
          value={query}
          placeholder={translate('address_book.placeholder')}
          style={{ paddingLeft: '10px', border: 'none' }}
          onKeyDown={e => {
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              highlight.prev();
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              highlight.next();
            }
            if (e.key === 'Enter') highlight.select();
          }}
        />
        {query && (
          <button
            /**
             * This is convenience:
             * a cross to clear input is nice, but it should not be the next thing to focus.
             * We use tabIndex to prevent this and then hitting tab hops to the next input.
             * */
            tabIndex={-1}
            type="button"
            className="address-book--clear-input"
            onClick={() => {
              document.getElementById(id)?.focus();
              setQuery('');
            }}
          >
            <IconCross style={{ width: '13px', height: '13px' }} />
          </button>
        )}
      </label>
      {addresses.isLoading && (
        <div className="address-book--space">
          <IconSmile spinning={true} />
        </div>
      )}
      {!addresses.isLoading &&
        results?.map((ha, i) => (
          <div
            key={ha['@id']}
            className={cn('address-book--result flex justify-between address-book--space', {
              'address-book--result--highlighted': highlight.current === i,
            })}
            onClick={() => handleSelect(ha)}
          >
            <div>
              <div>
                <b>{ha.locality}</b>
              </div>
              <div>
                {ha.line1} {ha.line2} {ha.postal_code} {ha.locality} {ha.administrative_area} {ha.country_code}
              </div>
            </div>
            <div>
              <img className="address-book--result-icon" src={PlusCircle} />
            </div>
          </div>
        ))}
      {!addresses.isLoading && results?.length === 0 && (
        <div className="address-book--space address-book--no-results">
          <div>
            <Translate id="address_book.no_results" />
          </div>
        </div>
      )}
    </div>
  );
};
